'use client';
import { ChevronLeftIcon, SearchIcon } from '@c/icons';
import { ChevronDown } from '@c/icons/chevron';
import Button from '@ui/Button';
import { cn, isMobile } from '@util/index';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import type { ChangeEvent, KeyboardEventHandler } from 'react';
import { forwardRef, useRef } from 'react';

export const InputStyles = cva(
  'flex flex-row items-center justify-between grow rounded-[1.2rem] gap-[0.8rem] relative text-[2rem] sm:text-[1.8rem]',
  {
    variants: {
      type: {
        search:
          'focus-within:border-[2px] focus-within:border-zinc-700 px-[1.6rem] py-[1.8rem] sm:py-[1.1rem]', // maybe these should be width props instead of based on type ?
        tel: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]', // maybe these should be width props instead of based on type ?
        email: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]', // maybe these should be width props instead of based on type ?
        password: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]',
        text: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]',
        number: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]',
        date: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]',
        time: 'px-[2.4rem] py-[1.8rem] sm:py-[1.1rem]',
        noPadding: '',
      },
      onDark: {
        true: 'bg-brand-white focus-within:bg-brand-white focus-within:shadow-[0_0_0_2px_#7c7a7a]',
        false: 'bg-white border-[1px] border-solid border-[#D9D9D9]',
      },
      full: {
        true: 'w-full max-w-full',
      },
      focus: {
        default:
          'focus-within:outline focus-within:outline-2 focus-within:outline-[#7c7a7a] focus-within:bg-white', // #7c7a7a is WCAG compliant 3:1 contrast ratio
      },
      error: {
        true: 'outline outline-2 outline-brand-red focus-within:outline-brand-red',
      },
      disabled: {
        true: 'bg-brand-gray-light text-brand-gray select-none focus-within:outline-none cursor-not-allowed ',
      },
      readonly: {
        true: 'bg-brand-gray-light text-brand-gray select-none focus-within:outline-none cursor-not-allowed ',
      },
      bordered: {
        true: 'border-[1px] border-color-[#8e8e8e]',
        false: 'border-transparent focus:border-transparent ',
      },
      noPadding: {
        true: '!px-0 !py-0',
      },
      rounded: {
        true: '!rounded-3xl',
      },
    },
    defaultVariants: {
      type: 'text',
      onDark: false,
      bordered: true,
    },
  }
);

interface Props extends VariantProps<typeof InputStyles> {
  placeholder: string;
  hideSearchIcon?: boolean;
  onClose?: () => void;
  onDark?: boolean;
  type:
    | 'tel'
    | 'search'
    | 'email'
    | 'text'
    | 'date'
    | 'number'
    | 'password'
    | 'time';
  onBlur?: () => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onClick?: () => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  center?: boolean;
  initialValue?: string;
  value?: string;
  trailingIconButton?: React.ReactNode;
  disabled?: boolean;
  bordered?: boolean;
  goBack?: () => void;
  searchIconRight?: boolean;
  showClearIcon?: boolean;
  autoComplete?: string;
  noPadding?: boolean;
  maxLength?: number;
  id?: string;
  autoFocus?: boolean;
  className?: string;
  iconClassName?: string;
  searchButtonClassName?: string;
  step?: number;
  onSearchButtonClick?: () => void;
  rounded?: boolean;
  onClearClick?: () => void;
  required?: boolean;
  min?: number;
  max?: number;
  ariaLabel?: string;
}

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      placeholder,
      type,
      onDark,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      focus,
      error,
      center,
      full,
      initialValue,
      value,
      trailingIconButton,
      disabled,
      bordered,
      goBack,
      searchIconRight,
      readonly,
      autoComplete,
      noPadding,
      required,
      maxLength,
      id,
      autoFocus,
      className,
      iconClassName,
      searchButtonClassName,
      hideSearchIcon,
      step,
      rounded,
      onSearchButtonClick,
      showClearIcon,
      onClearClick,
      min,
      max,
      ariaLabel,
    },
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = ref || internalRef;

    const AreaCodeSelect = () => (
      <div className="mr-[1.6rem] flex flex-row items-center gap-[0.8rem]">
        <span className="text-input font-semibold text-brand-white">+1</span>
        <ChevronDown />
      </div>
    );

    return (
      <div
        className={InputStyles({
          type,
          onDark,
          focus,
          error,
          full,
          disabled,
          bordered,
          noPadding,
          rounded,
          className,
        })}
      >
        {goBack && (
          <Button
            leadingIcon={<ChevronLeftIcon />}
            type="text"
            width="small"
            noXPadding
            onClick={goBack}
          />
        )}

        {type === 'search' && !searchIconRight && !hideSearchIcon && (
          <SearchIcon
            className={`h-full w-auto !text-black ${iconClassName}`}
          />
        )}

        {type === 'tel' && <AreaCodeSelect />}

        <input
          ref={inputRef}
          className={`w-full border-none border-transparent bg-transparent leading-input  focus:ring-0 sm:text-input ${
            center ? 'text-center' : ''
          } ${className}`}
          id={id ?? 'search-box-input'}
          value={value}
          type={type}
          placeholder={placeholder}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          defaultValue={initialValue}
          disabled={!!disabled}
          maxLength={maxLength}
          readOnly={!!readonly}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          step={step}
          required={required}
          min={min}
          max={max}
          aria-label={ariaLabel}
        />

        {trailingIconButton}

        {showClearIcon && !isMobile() && (
          <button
            type="button"
            aria-label="clear search"
            onClick={onClearClick}
            className="grouo mr-12 h-9 w-9 shrink-0 rounded-full p-1 transition-colors duration-75 ease-linear hover:bg-zinc-200"
          >
            <XIcon className="h-full w-full text-zinc-600 group-hover:text-zinc-800" />
          </button>
        )}

        {type === 'search' && searchIconRight && !hideSearchIcon && (
          <div className="flex h-full items-center gap-x-2">
            {showClearIcon && isMobile() && (
              <button
                type="button"
                aria-label="Clean search"
                className="absolute right-20 flex sm:hidden"
                onClick={() => {
                  if (onClearClick) {
                    onClearClick();
                  }
                }}
              >
                <XIcon className="h-10 w-10 text-zinc-600" />
              </button>
            )}

            <button
              type="button"
              aria-label="search"
              className={cn(
                'flex h-full items-center rounded-br-[1.73rem] rounded-tr-[1.73rem] bg-brand-secondary px-4',
                searchButtonClassName
              )}
              onClick={() => {
                if (onSearchButtonClick) {
                  onSearchButtonClick();
                }
              }}
            >
              <SearchIcon className={`h-8 w-8 !text-white ${iconClassName}`} />
            </button>
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
